namespace Portico.Services {

	import Inject = Portico.Decorators.Inject;
	import Service = Portico.Decorators.Service;

	import pm = Portico.Models;
	import pme = Portico.Models.Enterprise;

	export interface ISessionService {
		getApplication: (name: string) => angular.IPromise<pme.Application>;
		saveApplication: (application: pme.Application) => angular.IPromise<pme.Application>;

		getExistingSession(): pm.SessionDetail;
		getJwt(): string;
		wipeLocalSessions(): void;
		saveNewSession(jwt: string, sessionDetail: pm.SessionDetail, request: pm.AuthenticationRequest, childSessionData: pm.ChildSessionData[]): void;
		updateCurrentSessionExpiration(expirationDate: Date): void;
	}

	@Service('app', 'sessionService')
	@Inject('$http', '$q', '$window', 'apiService', 'dataService', 'serverConstants', 'storageService')
	export class SessionService implements ISessionService {
		constructor(
			protected $http: ng.IHttpService,
			protected $q: ng.IQService,
			protected $window: ng.IWindowService,
			protected apiService: IApiService,
			protected dataService: Portico.Services.IDataService,
			protected serverConstants: Portico.Models.Interfaces.IServerConstants,
			protected storageService: Portico.Services.IStorageService,
		) {
			this.$http.defaults.headers.common['accept'] = 'application/json';
			this.$http.defaults.headers.common["X-Requested-With"] = 'XMLHttpRequest';
		}

		getApplication(name: string): angular.IPromise<pme.Application> {
			const endpoint = `/sessionapplication/${name}`;

			return this.apiService.getItem<pme.Application>(endpoint).then((response) => {
				return new pme.Application(response);
			});
		}

		saveApplication(application: pme.Application): angular.IPromise<pme.Application> {
			const endpoint = `/sessionapplication`;

			return this.apiService.saveItem<pme.Application, pme.Application>(endpoint, application).then((response: pme.IApplication) => {
				return new pme.Application(response);
			});
		}

		/****** Public Methods ******/

		// Gets the current main/parent session
		public getExistingSession(): pm.SessionDetail {
			const sessionKeyName = this.getSessionKeyName();
			return this.storageService.getItem(pm.StorageType.Session, sessionKeyName) as pm.SessionDetail;
		}

		// Updates the current main/parent session and child sessions if needed
		public updateCurrentSessionExpiration(expirationDate: Date): void {
			const parentPortalId: string = this.storageService.getItem(pm.StorageType.Session, 'parent');
			const sessionKeyName = this.getSessionKeyName();
			const existingSession = this.getExistingSession();

			existingSession.status = Models.SESSION_ACTIVE;
			existingSession.expirationDate = expirationDate;
			this.storageService.saveItem(Models.StorageType.Session, sessionKeyName, existingSession);

			if (parentPortalId) {
				this.touchChildExpirations(parentPortalId, existingSession.expirationDate);
			}
		}

		// Gets the JWT for the main/parent session
		public getJwt(): string {
			const jwtTokenName = this.getJwtKeyName();
			return this.storageService.getItem(Models.StorageType.Session, jwtTokenName);
		}

		// Used to save a new main/parent session
		public saveNewSession(jwt: string, sessionDetail: pm.SessionDetail,
			request: pm.AuthenticationRequest,
			childSessionData: pm.ChildSessionData[] = null): void {

			this.storageService.saveItem(Models.StorageType.Session, this.dataService.jwtTokenKeyName, jwt);
			this.storageService.saveItem(Models.StorageType.Session, this.dataService.sessionKeyName, sessionDetail);

			if (sessionDetail.isParent) {
				this.storageService.saveItem(Models.StorageType.Session, 'parent', request.portalId);
				this.persistChildSessions(request.portalId, childSessionData, sessionDetail.expirationDate, request.userName);
			}
		}

		// Erases all session key data from local session storage
		public wipeLocalSessions(): void {
			const parentPortalId: string = this.storageService.getItem(pm.StorageType.Session, 'parent') ?
				this.storageService.getItem(pm.StorageType.Session, 'parent') : this.serverConstants.portalId;

			const parentSession = this.storageService.getItem(pm.StorageType.Session, `${pm.SESSION_STORAGE_KEY}-${parentPortalId}`) as pm.SessionDetail;

			if (parentSession.isParent) {
				this.storageService.deleteItem(pm.StorageType.Session, 'parent');

				// delete child sessions
				for (var x in sessionStorage) {
					if (x.indexOf('ngStorage-session-') > -1 && JSON.parse(sessionStorage[x]).hasOwnProperty("parentPortalId")) {
						if (JSON.parse(sessionStorage[x]).parentPortalId === parentPortalId) {
							const portalId = x.replace('ngStorage-session-', '');

							// delete the session
							this.storageService.deleteItem(pm.StorageType.Session, x.replace('ngStorage-', ''));
							// delete the JWT
							this.storageService.deleteItem(pm.StorageType.Session, `${pm.JWT_STORAGE_KEY}-${portalId}`)
						}
					}
				}
			}

			this.storageService.deleteItem(pm.StorageType.Session, `${pm.JWT_STORAGE_KEY}-${parentPortalId}`);
			this.storageService.deleteItem(pm.StorageType.Session, `${pm.SESSION_STORAGE_KEY}-${parentPortalId}`);
		}

		/****** Private Methods ******/

		// Updates any child session expiration dates for the given parent portal
		private touchChildExpirations(parentPortalId, expirationDate: Date): void {
			for (var x in sessionStorage) {
				if (x.indexOf('ngStorage-session-') > -1) {
					let sessionDetail = JSON.parse(sessionStorage[x]);

					if (sessionDetail.hasOwnProperty("parentPortalId") && sessionDetail.parentPortalId === parentPortalId) {
						const childSession = angular.copy(sessionDetail) as pm.SessionDetail;
						childSession.expirationDate = expirationDate;
						this.storageService.saveItem(Models.StorageType.Session, x.replace('ngStorage-', ''), childSession);
					}
				}
			}
		}

		// saves child sessions to local SessionStorage
		private persistChildSessions(parentPortalId: string, childSessions: pm.ChildSessionData[], expirationDate: Date, userName: string): void {
			childSessions.forEach((childSession: pm.ChildSessionData) => {
				const jwtName = `${pm.JWT_STORAGE_KEY}-${childSession.portalId}`;
				const sessionDetailName = `${pm.SESSION_STORAGE_KEY}-${childSession.portalId}`;
				const sessionDetail = {
					expirationDate: expirationDate,
					status: Models.SESSION_ACTIVE,
					features: childSession.features,
					isParent: false,
					parentPortalId: parentPortalId,
					userName: userName
				} as pm.SessionDetail;

				this.storageService.saveItem(Models.StorageType.Session, jwtName, childSession.jwt);
				this.storageService.saveItem(Models.StorageType.Session, sessionDetailName, sessionDetail);
			});
		}

		// get the main/parent jwt token key name
		private getJwtKeyName(): string {
			const parentPortalId: string = this.storageService.getItem(pm.StorageType.Session, 'parent');
			return (parentPortalId !== null && parentPortalId !== undefined) ? `${pm.JWT_STORAGE_KEY}-${parentPortalId}` : this.dataService.jwtTokenKeyName;
		}

		// get the main/parent session key name
		private getSessionKeyName(): string {
			const parentPortalId: string = this.storageService.getItem(pm.StorageType.Session, 'parent');
			return (parentPortalId !== null && parentPortalId !== undefined) ? `${pm.SESSION_STORAGE_KEY}-${parentPortalId}` : this.dataService.sessionKeyName;
		}
	}
}